/* eslint-disable tailwindcss/no-arbitrary-value */
'use client';

import {
  formatLocalIsoDate,
  parseIsoDate,
} from '@hyphengroup/cms-components/utils/helpers';
import { Checkbox, Tooltip } from '@material-tailwind/react';
import classNames from 'classnames';
import { ModalOverlay, useModal } from 'components/molecules/Modal/Modal';
import { differenceInCalendarDays } from 'date-fns';
import { useMemo, useState } from 'react';
import {
  CmsCountry,
  CmsRegion,
  TravelInsuranceType,
} from '../../../../../app/[site]/[locale]/(cms)/_modules/cmsService/types';
import { TravelCompareQuotesParams } from '../../../../../app/[site]/[locale]/(cms)/_modules/travelQuotesService/types';
import { CoverTypeToggle } from '../CoverTypeToggle/CoverTypeToggle';
import Translations from './i18n';
import {
  trackTiDateSelectionConfirm,
  trackTiDateSelectionStart,
  trackTiDateUnavailableClick,
  trackTripDetailsCoverType,
  trackTripDetailsDateChange,
  trackTripDetailsDestinationRemoval,
  trackTripDetailsDestinationsChange,
  trackTripDetailsHover,
  trackTripDetailsTravellersChange,
} from './tracking';
import { TIOptionsValues } from './useTIOptions';
import Button from '$/components/Button';
import { CountryChipPicker } from '$/components/CountryChipPicker/CountryChipPicker';
import type { DestinationOption } from '$/components/CountryChipPicker/types';
import { DateRangePicker } from '$/components/DateRangePicker/DateRangePicker';
import type { UnavailableReason } from '$/components/DateRangePicker/unavailableReason';
import FormFieldLabel from '$/components/FormFieldLabel';
import Close from '$/components/icons/Close';
import Info from '$/components/icons/Info';
import Search from '$/components/icons/Search';
import NumberSelector from '$/components/NumberSelector/NumberSelector';
import { Portal } from '$/components/Portal';
import { useClientContext } from '$/modules/context/client';
import { getClientTranslations } from '$/modules/i18n/client.index';

type TIOptionsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  searchParams: TravelCompareQuotesParams;
  onChangeParams: (value: TravelCompareQuotesParams) => void;
  countries: CmsCountry[];
  regions: CmsRegion[];
  boundaries: {
    minChildren?: number;
    maxChildren?: number;
    minAdult?: number;
    maxAdult?: number;
    maxTripDuration?: number;
    offsetStartDate?: number;
    tripType?: TravelInsuranceType;
    advancePurchaseDuration?: number;
    defaultAdult?: number;
    defaultChildren?: number;
    defaultTripDuration?: number;
    popularDestinations?: CmsCountry[];
    destinationCountries?: CmsCountry[];
    destinationRegion?: CmsRegion;
    excludeDestinations?: CmsCountry[];
  };
  values: TIOptionsValues;
};

export const TIOptionsModal = (props: TIOptionsModalProps) => {
  const context = useClientContext();
  const staticText = getClientTranslations(Translations, context);
  const { isOpen, searchParams, onChangeParams, onClose, regions, values } =
    props;

  const {
    countryMap,
    countryOptions,
    minDate,
    maxDate,
    maxDuration,
    minAdults,
    maxAdults,
    minChildren,
    maxChildren,
    normalizeParams,
    availableTripTypes,
  } = values;

  const [localValues, setLocalValues] = useState<
    Partial<TravelCompareQuotesParams>
  >({});

  // skipDestination keeps the raw destination so clearing the last one stays
  // empty (and handlers spreading formParams don't re-add the default back).
  const formParams: TravelCompareQuotesParams = normalizeParams(
    { ...searchParams, ...localValues },
    { skipDestination: true },
  );

  const isSingleTrip = formParams.insuranceType === 'SINGLE_PLAN';
  const regionOption = {
    value: formParams.region || regions[0]?.value || '',
    label:
      regions.find((r) => r.value === formParams.region)?.label ||
      regions[0]?.label ||
      '',
  };
  const selectedCountries = useMemo<DestinationOption[]>(() => {
    if (!formParams.destinationIsoCodes) return [];
    return formParams.destinationIsoCodes
      .map((code) => ({
        value: code,
        label: countryMap.get(code) || code,
      }))
      .filter((opt) => opt.label !== opt.value);
  }, [formParams.destinationIsoCodes, countryMap]);

  const startDateObj = parseIsoDate(formParams.startDate);
  const endDateObj = parseIsoDate(formParams.endDate);

  // Check if travel as family is eligible
  const isEligibleTravelAsFamily =
    !!formParams.noOfChildren &&
    formParams.noOfAdults > 0 &&
    formParams.noOfAdults <= 2;

  // Base the blocking rule on the actual selection, not the display list:
  // selectedCountries drops codes without a label in countryMap, which could
  // disable the CTA even when a destination is selected.
  const isDestinationEmpty =
    isSingleTrip && !formParams.destinationIsoCodes?.length;

  const handleSubmit = () => {
    onChangeParams(formParams);
    setLocalValues({});

    onClose();
  };

  // Closing never commits — only an explicit Search press applies the new params.
  const handleDismiss = () => {
    setLocalValues({});
    onClose();
  };

  const handleCoverTypeChange = (
    value: TravelCompareQuotesParams['insuranceType'],
  ) => {
    trackTripDetailsCoverType(
      value === 'SINGLE_PLAN' ? 'Single Trip' : 'Annual',
      { isOverlay: true },
    );
    // Normalize without skipDestination so switching back to single re-applies
    // the default destination (a toggle isn't an explicit clear).
    setLocalValues(
      normalizeParams({
        ...formParams,
        insuranceType: value,
        endDate: '',
      }),
    );
  };

  const handleSingleTripDestinationChange = (
    selected: DestinationOption[] | null,
  ) => {
    const codes = Array.isArray(selected)
      ? selected.map((opt) => opt.value)
      : [];
    const removedDestinations = selectedCountries.filter(
      (country) => !codes.includes(country.value),
    );
    const hasRemovedDestinations = removedDestinations.length > 0;
    if (hasRemovedDestinations) {
      trackTripDetailsDestinationRemoval(
        removedDestinations.map((country) => country.label).join(', '),
        { isOverlay: true },
      );
    }
    const destinationLabel = codes
      .map((code) => countryMap.get(code) || code)
      .join(', ');
    if (!hasRemovedDestinations) {
      trackTripDetailsDestinationsChange(destinationLabel || '', {
        isOverlay: true,
      });
    }
    setLocalValues({
      ...formParams,
      destinationIsoCodes: codes.length > 0 ? codes : undefined,
    });
  };

  const handleRegionChange = (selected: DestinationOption | null) => {
    if (selected) {
      trackTripDetailsDestinationsChange(selected.label, { isOverlay: true });
    }
    setLocalValues({
      ...formParams,
      region: selected?.value,
    });
  };

  // CMS-1853 discrete date events share market/brand/tripType params.
  const dateEventContext = {
    market: context.lang.split('-')[1]?.toUpperCase() ?? '',
    brand: context.brand,
    tripType: formParams.insuranceType ?? '',
    viewport: 'mobile' as const,
  };

  const handleDateSelectionStart = () => {
    trackTiDateSelectionStart(dateEventContext);
  };

  const handleDateUnavailableClick = (
    _date: Date,
    reason: UnavailableReason,
  ) => {
    trackTiDateUnavailableClick({ ...dateEventContext, reason });
  };

  const handleDateRangeSelect = (
    range: { from?: Date; to?: Date } | undefined,
  ) => {
    const newValues: Partial<TravelCompareQuotesParams> = {
      ...formParams,
    };

    if (range?.from) {
      newValues.startDate = formatLocalIsoDate(range.from);
    }
    if (range?.to) {
      newValues.endDate = formatLocalIsoDate(range.to);
    } else if (!range?.from) {
      // Clear dates if range is cleared
      newValues.startDate = '';
      newValues.endDate = '';
    }

    if (newValues.startDate && newValues.startDate !== formParams.startDate) {
      trackTripDetailsDateChange('start', newValues.startDate, {
        isOverlay: true,
      });
    }
    if (newValues.endDate && newValues.endDate !== formParams.endDate) {
      trackTripDetailsDateChange('end', newValues.endDate, { isOverlay: true });
    }

    // Confirm commits the selection — fire the discrete confirm event with
    // the final range (dates computed at event time, not render time).
    if (range?.from && range?.to) {
      trackTiDateSelectionConfirm({
        ...dateEventContext,
        startDate: formatLocalIsoDate(range.from),
        endDate: formatLocalIsoDate(range.to),
        tripDurationDays: differenceInCalendarDays(range.to, range.from) + 1,
        advanceDays: differenceInCalendarDays(range.from, new Date()),
      });
    }

    setLocalValues(newValues);
  };

  const handleAdultChange = (value: number) => {
    const previousAdults = formParams.noOfAdults ?? 0;
    if (value !== previousAdults) {
      trackTripDetailsTravellersChange({
        travellerType: 'adult',
        action: value > previousAdults ? 'plus' : 'minus',
        value,
        isOverlay: true,
      });
    }
    setLocalValues({
      ...formParams,
      noOfAdults: value,
    });
  };

  const handleChildrenChange = (value: number) => {
    const previousChildren = formParams.noOfChildren ?? 0;
    if (value !== previousChildren) {
      trackTripDetailsTravellersChange({
        travellerType: 'child',
        action: value > previousChildren ? 'plus' : 'minus',
        value,
        isOverlay: true,
      });
    }
    setLocalValues({
      ...formParams,
      noOfChildren: value,
    });
  };

  const handleTravelAsFamilyChange = () => {
    setLocalValues({
      ...formParams,
      travelAsFamily: !formParams.travelAsFamily,
    });
  };

  // Use the modal hook for scroll locking and ESC key handling
  useModal({ isScrollLockActive: isOpen, onClose: handleDismiss });

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div className="flex h-full flex-col bg-fill/card">
      <div className="flex items-center gap-spacing/gap/soft-12px border-b border-borders/default p-spacing/padding/y-sm bg-fill/card">
        <h2 className="flex-1 font-semibold text-text/primary text-font-size/subheading-1 leading-line-height/subheading-1">
          {staticText.modalTitle}
        </h2>
        <button
          type="button"
          onClick={handleDismiss}
          className="flex size-5 items-center justify-center text-text/primary"
          aria-label={staticText.close}
        >
          <Close className="size-full" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col gap-spacing/gap/soft-12px px-spacing/padding/x py-spacing/padding/y">
          <div className="flex flex-col gap-spacing/gap/soft-6px">
            <CoverTypeToggle
              value={formParams.insuranceType}
              availableTripTypes={availableTripTypes}
              onChange={handleCoverTypeChange}
              onTooltipHover={(label) => trackTripDetailsHover(label)}
            />
          </div>

          <div className="flex flex-col gap-spacing/gap/soft-6px">
            <FormFieldLabel
              label={staticText.destinationLabel}
              tooltip={staticText.destinationTooltip}
              id="ti-destination-modal"
              onTooltipHover={() =>
                trackTripDetailsHover(staticText.destinationLabel)
              }
            />
            {isSingleTrip ? (
              <CountryChipPicker
                instanceId="ti-destination-modal"
                inputId="ti-destination-modal"
                name="ti-destination-modal"
                isMulti
                mode="countries"
                maxVisibleValues={2}
                value={selectedCountries}
                options={countryOptions}
                onChange={(selected) =>
                  handleSingleTripDestinationChange(
                    selected as DestinationOption[] | null,
                  )
                }
              />
            ) : (
              <CountryChipPicker
                instanceId="ti-region-modal"
                inputId="ti-destination-modal"
                name="ti-destination-modal"
                mode="select"
                value={regionOption}
                options={regions}
                onChange={(selected) =>
                  handleRegionChange(selected as DestinationOption | null)
                }
              />
            )}
          </div>

          <div className="flex flex-col gap-spacing/gap/soft-6px">
            <FormFieldLabel
              label={staticText.travelDatesLabel}
              tooltip={staticText.travelDatesTooltip}
              onTooltipHover={() =>
                trackTripDetailsHover(staticText.travelDatesLabel)
              }
            />
            <DateRangePicker
              startDate={startDateObj}
              endDate={endDateObj}
              mode={isSingleTrip ? 'range' : 'annual'}
              displayVariant="travel"
              onSelect={handleDateRangeSelect}
              onSelectionStart={handleDateSelectionStart}
              onUnavailableClick={handleDateUnavailableClick}
              minDate={minDate}
              maxDate={maxDate}
              duration={maxDuration}
            />
          </div>

          <div className="flex gap-spacing/gap/soft-12px">
            <div className="flex-1">
              <NumberSelector
                value={formParams.noOfAdults}
                onChange={handleAdultChange}
                minValue={minAdults}
                maxValue={maxAdults}
                step={1}
                label={staticText.adultsLabel}
                tooltip={staticText.adultsTooltip}
                hideInput
                id="ti-adults-modal"
                onTooltipHover={() =>
                  trackTripDetailsHover(staticText.adultsLabel)
                }
              />
            </div>

            <div className="flex-1">
              <NumberSelector
                value={formParams.noOfChildren}
                onChange={handleChildrenChange}
                minValue={minChildren}
                maxValue={maxChildren}
                step={1}
                label={staticText.childrenLabel}
                tooltip={staticText.childrenTooltip}
                hideInput
                id="ti-children-modal"
                onTooltipHover={() =>
                  trackTripDetailsHover(staticText.childrenLabel)
                }
              />
            </div>
          </div>

          {isEligibleTravelAsFamily && (
            <div className="flex items-center">
              <Checkbox
                className="before:hidden checked:border-primary-800 checked:bg-primary-800 checked:before:bg-primary-800 hover:!border-border/form-hover hover:shadow-md"
                checked={formParams.travelAsFamily}
                containerProps={{
                  className: 'p-0 mr-2 rounded-md self-center',
                }}
                labelProps={{
                  className:
                    'alpha-form-input text-base md:text-lg p-0 text-text/primary',
                }}
                label={staticText.travelingAsFamilyLabel}
                onChange={handleTravelAsFamilyChange}
                ripple={false}
              />
              <Tooltip
                content={
                  // eslint-disable-next-line tailwindcss/no-arbitrary-value
                  <div className="max-w-[242px] md:max-w-65 lg:max-w-[286px] xl:max-w-[322px]">
                    {staticText.travelingAsFamilyTooltip}
                  </div>
                }
                placement="top"
              >
                <span className="ml-1 inline-flex items-center">
                  <Info className="inline-block size-shapes/icon-size-md align-middle text-secondary-lighter" />
                </span>
              </Tooltip>
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-borders/default p-spacing/gap/y-sm bg-fill/card">
        <Button
          data-testid="ti-search-submit"
          onClick={handleSubmit}
          disabled={isDestinationEmpty}
          styleType="primary"
          className="w-full"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
        >
          <Search className="size-shapes/icon-size-md" />
          {staticText.findBestDealButton}
        </Button>
      </div>
    </div>
  );

  // Portalled to body and lifted onto the modal layer as one group, so page
  // chrome such as the TI provider page's sticky footer CTA (z-60) can't cover
  // it (CMS-2040). The portal matters as much as the z-index: while TIOptions
  // is pinned, GlobalStickyPanel is a fixed z-51 stacking context that would
  // cap anything rendered inside it.
  return (
    <Portal>
      <div
        data-component="ti-options-modal"
        className="relative z-[--z-index-modal]"
      >
        <ModalOverlay onClose={handleDismiss} />
        <div
          className={classNames(
            'fixed inset-x-0 bottom-0 z-50 max-h-[90vh] overflow-hidden rounded-t-lg shadow-xl',
            'md:hidden',
          )}
        >
          {modalContent}
        </div>

        {/* Tablet and Desktop: Centered Modal */}
        <div
          className={classNames(
            'fixed left-1/2 top-1/2 z-50 hidden w-full max-w-2xl overflow-hidden rounded-radius/card-inner shadow-xl',
            '-translate-x-1/2 -translate-y-1/2',
            'md:block',
          )}
        >
          {modalContent}
        </div>
      </div>
    </Portal>
  );
};
